import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchLoyalCustomersByCount,
  LoyalCustomer,
} from '@/services/analyticsService';
import { useTopProductsByMonth } from '@/hooks/useTopProductsByMonth';
import { useAuth } from '@/hooks/useAuth';
import { useMemberPreference } from '@/hooks/useMemberPreference';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/supabase/client';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameMonth, isSameDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarDays, Users, ShoppingBag, AlertCircle, Banknote, CreditCard } from 'lucide-react'; // Removed Info
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

// Tooltip imports removed as they are no longer used for this purpose

// TODO: Define types for sales data, customer data, item data
interface DailySale {
  date: Date;
  amount: number;
  cashAmount: number;
  cardAmount: number;
  isPlaceholder?: boolean; // For days not in the current month
}

interface MonthlySummary {
  actualSales: number;
  actualSalesCount: number;
  cashAmount: number;
  cashCount: number;
  cardAmount: number;
  cardCount: number;
  unspecifiedAmount: number;
  unspecifiedCount: number;
}

// Placeholder for subscription plan type, adapt from MyPage.tsx if needed
type PlanId = 'free' | 'standard' | 'premium';

const PlanFeatures = {
  free: {
    canViewDailySales: true,
    canViewMonthlySales: false,
    canViewLoyalCustomers: false,
    canViewTopItems: false,
  },
  standard: {
    canViewDailySales: true,
    canViewMonthlySales: true,
    canViewLoyalCustomers: false,
    canViewTopItems: false,
  },
  premium: {
    canViewDailySales: true,
    canViewMonthlySales: true,
    canViewLoyalCustomers: true,
    canViewTopItems: true,
  },
};

const BlurredSectionWrapper: React.FC<{
  children: React.ReactNode;
  isAllowed: boolean;
  message: string;
  blurClass?: string; // 추가된 prop
}> = ({ children, isAllowed, message, blurClass = 'blur-sm' }) => { // 기본값 설정
  if (isAllowed) {
    return <>{children}</>;
  }
  return (
    <div className="relative group">
      <div className={`${blurClass} filter transition-all duration-300`}>{children}</div> {/* 동적으로 클래스 적용 */}
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg">
        <div className="text-center p-5 bg-white/90 backdrop-blur-md rounded-xl shadow-2xl max-w-xs mx-auto">
          <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
          <p className="mt-3 text-sm font-semibold text-slate-700">{message}</p>
        </div>
      </div>
    </div>
  );
};
const formatKoreanManUnit = (amount: number): string => {
  if (amount <= 0) {
    return '0원';
  }
  // Round to the nearest 10,000 (만)
  const manAmount = amount / 10000;
  const roundedMan = Math.round(manAmount);

  if (roundedMan === 0) {
    // For any positive amount that rounds to 0만 (e.g., less than 5,000원),
    // display as "1만" as per the example "1만, 2만..." for small amounts.
    return '1만';
  } else {
    return `${roundedMan}만`;
  }
};

const ADMIN_USER_ID = 'bd873ddc-9c11-4093-a4e6-a2fb74e2cb71';

export default function TodaySalesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscriptionPlan, isLoading: subscriptionLoading } = useSubscription();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailySales, setDailySales] = useState<DailySale[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true); // For calendar and monthly summary
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true); // For loyal customers

  // State for actual premium feature data
  const [actualLoyalCustomers, setActualLoyalCustomers] = useState<LoyalCustomer[]>([]);

  // Admin-only: target user ID for viewing other users' data
  const [targetUserId, setTargetUserId] = useState<string>('');
  const isAdmin = user?.id === ADMIN_USER_ID;
  const effectiveUserId = isAdmin && targetUserId ? targetUserId : user?.id;

  const currentPlanId: PlanId = subscriptionPlan?.id as PlanId || 'free';
  const features = PlanFeatures[currentPlanId];

  const topItemsMonth = format(currentDate, 'yyyy-MM-01');
  const {
    items: actualTopItems,
    isLoading: isTopItemsLoading,
    error: topItemsError,
  } = useTopProductsByMonth(topItemsMonth, !!user && !subscriptionLoading && features.canViewTopItems);

  const { recordPaymentMethod } = useMemberPreference({ user });
  // admin target 모드에서는 미표시 (대상 셀러의 opt-in 여부를 알 수 없으므로 숨김 — 스펙 §4)
  const showPaymentBreakdown = recordPaymentMethod && !(isAdmin && !!targetUserId);

  const handleDateClick = (day: Date) => {
    const formattedDate = format(day, 'yyyy-MM-dd');
    navigate(`/order?date=${formattedDate}`);
  };

  const fetchSalesData = useCallback(async (month: Date) => {
    if (!user || !effectiveUserId) return;
    setIsLoading(true);
    
    // Placeholder for fetching logic
    // 1. Fetch orders from 'order' table for the user and month.
    // 2. Filter orders based on status ('입금 완료' or '출고 완료').
    // 3. Calculate daily sales and monthly summary.

    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);

    const { data: orders, error: ordersError } = await supabase
      .from('order')
      .select('id, user_id, order_date, total_price, status, payment_method') // Added status
      .eq('user_id', effectiveUserId)
      .gte('order_date', format(monthStart, 'yyyy-MM-dd'))
      .lte('order_date', format(monthEnd, 'yyyy-MM-dd'));

    if (ordersError) {
      console.error("Error fetching orders:", ordersError);
      setIsLoading(false);
      return;
    }

    if (!orders || orders.length === 0) {
      setDailySales([]);
      setMonthlySummary({
        actualSales: 0, actualSalesCount: 0,
        cashAmount: 0, cashCount: 0, cardAmount: 0, cardCount: 0,
        unspecifiedAmount: 0, unspecifiedCount: 0,
      });
      setIsLoading(false);
      return;
    }

    // Filter orders by status
    const confirmedOrders = orders.filter(order =>
      order.status === '입금 완료' || order.status === '출고 완료'
    );
    
    let totalMonthSales = 0;
    let totalMonthSalesCount = 0;
    let cashAmount = 0, cashCount = 0;
    let cardAmount = 0, cardCount = 0;
    let unspecifiedAmount = 0, unspecifiedCount = 0;

    const salesByDay: { [key: string]: { total: number; cash: number; card: number } } = {};

    confirmedOrders.forEach(order => {
      // Handles both string and Date object types returned from Supabase
      const dayKey = format(new Date(order.order_date), 'yyyy-MM-dd');
      if (!salesByDay[dayKey]) {
        salesByDay[dayKey] = { total: 0, cash: 0, card: 0 };
      }
      salesByDay[dayKey].total += order.total_price;
      totalMonthSales += order.total_price;
      totalMonthSalesCount++;
      if (order.payment_method === 'cash') {
        cashAmount += order.total_price; cashCount++;
        salesByDay[dayKey].cash += order.total_price;
      } else if (order.payment_method === 'card') {
        cardAmount += order.total_price; cardCount++;
        salesByDay[dayKey].card += order.total_price;
      } else {
        unspecifiedAmount += order.total_price; unspecifiedCount++;
      }
    });

    const daysInDisplayMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const calculatedDailySales: DailySale[] = daysInDisplayMonth.map(day => {
      const dayKey = format(day, 'yyyy-MM-dd');
      return {
        date: day,
        amount: salesByDay[dayKey]?.total || 0,
        cashAmount: salesByDay[dayKey]?.cash || 0,
        cardAmount: salesByDay[dayKey]?.card || 0,
      };
    });
    
    setDailySales(calculatedDailySales);
    setMonthlySummary({
      actualSales: totalMonthSales,
      actualSalesCount: totalMonthSalesCount,
      cashAmount, cashCount, cardAmount, cardCount,
      unspecifiedAmount, unspecifiedCount,
    });

    setIsLoading(false);
  }, [user, effectiveUserId]);

  useEffect(() => {
    // Fetches daily/monthly sales data tied to currentDate
    if (user && !subscriptionLoading) {
      fetchSalesData(currentDate); // This sets isLoading for its own part
    } else if (!user && !subscriptionLoading) {
      // Handle non-logged-in state or prompt login
      setDailySales([]);
      setMonthlySummary(null);
      setIsLoading(false); // Ensure calendar/monthly loading is off
    }
  }, [user, currentDate, fetchSalesData, subscriptionLoading]);

  useEffect(() => {
    // Fetches loyal customers (top items: useTopProductsByMonth)
    const fetchAnalyticsData = async () => {
      if (!user) {
        setActualLoyalCustomers([]);
        setIsAnalyticsLoading(false);
        return;
      }

      setIsAnalyticsLoading(true);
      try {
        const loyalCustomersResult = features.canViewLoyalCustomers
          ? await fetchLoyalCustomersByCount(20)
          : [];

        if (features.canViewLoyalCustomers) {
          setActualLoyalCustomers(loyalCustomersResult);
        }

      } catch (error) {
        console.error("Error fetching analytics data:", error);
        setActualLoyalCustomers([]); // Clear on error
      } finally {
        setIsAnalyticsLoading(false);
      }
    };

    if (user && !subscriptionLoading) {
      fetchAnalyticsData();
    } else {
      // Clear data if user logs out or subscription is still loading
      setActualLoyalCustomers([]);
      setIsAnalyticsLoading(false);
    }
  }, [user, subscriptionLoading, features.canViewLoyalCustomers, effectiveUserId]);

  const handlePrevMonth = () => {
    setCurrentDate(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => addMonths(prev, 1));
  };

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return days.map(day => {
      const saleData = dailySales.find(s => isSameDay(s.date, day));
      const isCurrentMonthDay = isSameMonth(day, currentDate);
      const isToday = isSameDay(day, new Date());
      const isSunday = getDay(day) === 0;

      let cellClasses = "p-3 border-b border-r text-sm min-h-[100px] flex flex-col justify-between transition-colors duration-150 "; // Increased min-height and padding
      if (isCurrentMonthDay) {
        cellClasses += isToday ? "bg-blue-50 hover:bg-slate-100 relative cursor-pointer" : "bg-white hover:bg-slate-50 cursor-pointer";
      } else {
        cellClasses += "bg-slate-50 text-slate-400";
      }
      
      let dayNumberClasses = "font-semibold ";
      if (isToday && isCurrentMonthDay) {
        dayNumberClasses += 'w-7 h-7 flex items-center justify-center rounded-full bg-blue-200 text-blue-700 self-end';
      } else {
        dayNumberClasses += "text-right ";
        if (isSunday && isCurrentMonthDay) {
          dayNumberClasses += 'text-red-600';
        } else if (isCurrentMonthDay) {
          dayNumberClasses += 'text-slate-800';
        } else {
          dayNumberClasses += 'text-slate-400';
        }
      }

      return (
        <div key={day.toString()} className={cellClasses} onClick={() => isCurrentMonthDay && handleDateClick(day)}>
          {isCurrentMonthDay ? (
            <>
              <div className={dayNumberClasses}>
                {format(day, 'd')}
              </div>
              {features.canViewDailySales && saleData && (
                <div>
                  <div className={`mt-1 text-xs font-bold self-start ${saleData.amount > 0 ? 'text-blue-700' : 'text-slate-500'}`}>
                    {saleData.amount === 0 ? (
                      '0원'
                    ) : (
                      <>
                        <span className="md:hidden">{formatKoreanManUnit(saleData.amount)}</span>
                        <span className="hidden md:inline">{`${saleData.amount.toLocaleString()}원`}</span>
                      </>
                    )}
                  </div>
                  {showPaymentBreakdown && (saleData.cashAmount !== 0 || saleData.cardAmount !== 0) && (
                    <div className="hidden md:flex flex-col gap-0.5 mt-1">
                      {saleData.cashAmount !== 0 && (
                        <span className="flex items-center gap-1 text-[11px] font-medium leading-none text-emerald-700">
                          <Banknote className="h-3 w-3 shrink-0" aria-hidden="true" />
                          현금 {saleData.cashAmount.toLocaleString()}원
                        </span>
                      )}
                      {saleData.cardAmount !== 0 && (
                        <span className="flex items-center gap-1 text-[11px] font-medium leading-none text-violet-700">
                          <CreditCard className="h-3 w-3 shrink-0" aria-hidden="true" />
                          카드 {saleData.cardAmount.toLocaleString()}원
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            // Render an empty cell for days not in the current month to maintain grid structure
            <div className="h-full"></div>
          )}
        </div>
      );
    });
  };
  
  const daysOfWeek = ["일", "월", "화", "수", "목", "금", "토"];

  if (subscriptionLoading || (user && isLoading)) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-slate-600">데이터를 불러오는 중입니다...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50">
        <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">로그인이 필요합니다</h2>
        <p className="text-slate-500">이 페이지를 보려면 먼저 로그인해주세요.</p>
        {/* TODO: Add login button or redirect */}
      </div>
    );
  }
  
  const mockLoyalCustomers = [
    { rank: 1, name: "김*아", value: "1,250,000원 (5회)" },
    { rank: 2, name: "이*현", value: "980,000원 (3회)" },
    { rank: 3, name: "박*준", value: "760,000원 (8회)" },
    { rank: 4, name: "최*윤", value: "650,000원 (2회)" },
    { rank: 5, name: "정*슬", value: "550,000원 (4회)" },
  ];

  const mockTopItems = [
    { rank: 1, name: "프리미엄 청바지", value: "150개 (3,000,000원)" },
    { rank: 2, name: "오버핏 후드티", value: "120개 (2,400,000원)" },
    { rank: 3, name: "데일리 스니커즈", value: "90개 (1,800,000원)" },
    { rank: 4, name: "코튼 볼캡", value: "85개 (850,000원)" },
    { rank: 5, name: "스트라이프 셔츠", value: "70개 (1,050,000원)" },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Admin-only: User ID input */}
        {isAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <label className="block text-sm font-semibold text-yellow-800 mb-2">
              어드민 전용: 조회할 유저의 Supabase user_id 입력
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="user_id를 입력하세요 (비워두면 본인 데이터 조회)"
                className="flex-1"
              />
              <Button
                onClick={() => {
                  // effectiveUserId가 변경되면 useEffect가 자동으로 데이터를 다시 불러옴
                  // 명시적으로 다시 불러오기 위해 fetchSalesData 호출
                  if (user && !subscriptionLoading) {
                    fetchSalesData(currentDate);
                  }
                }}
                variant="outline"
                className="bg-white hover:bg-slate-50"
              >
                조회
              </Button>
            </div>
            {targetUserId && (
              <p className="text-xs text-yellow-700 mt-2">
                현재 조회 중인 user_id: {targetUserId}
              </p>
            )}
          </div>
        )}
        
        <header className="mb-6 md:mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <Button variant="outline" size="icon" onClick={handlePrevMonth} className="bg-white hover:bg-slate-50 border-slate-300 shadow-sm">
              <ChevronLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 tracking-tight">{format(currentDate, 'yyyy년 MM월', { locale: ko })}</h1>
            <Button variant="outline" size="icon" onClick={handleNextMonth} className="bg-white hover:bg-slate-50 border-slate-300 shadow-sm">
              <ChevronRight className="h-5 w-5 text-slate-600" />
            </Button>
          </div>
        </header>
  
        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
          {/* Monthly Summary Section */}
          <div className="bg-slate-50 p-6 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-700 mb-4">월별 요약</h3>
            <BlurredSectionWrapper
              isAllowed={features.canViewMonthlySales}
              message="월별 매출 정보는 스탠다드 플랜 이상부터 제공됩니다."
              blurClass="blur-md" // "월별 요약" 섹션에만 blur-md 적용
            >
              {monthlySummary ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-baseline p-4 bg-blue-50 rounded-lg border border-blue-200"> {/* Changed bg-blue-100 to bg-blue-50 */}
                    <div>
                      <span className="text-sm font-medium text-blue-800">총 매출액</span>
                      <p className="text-xs text-blue-700">({monthlySummary.actualSalesCount}건의 확정된 주문)</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        확정된 주문이란 정산서 페이지의 '입금 완료' 또는 '출고 완료' 상태인 주문입니다.
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        배송비는 매출에 포함되지 않습니다.
                      </p>
                    </div>
                    <span className="font-bold text-blue-800 text-xl md:text-2xl">{monthlySummary.actualSales.toLocaleString()}원</span>
                  </div>
                  {showPaymentBreakdown && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800 mb-2">결제수단별 매출</p>
                      <div className="space-y-1 text-sm text-slate-700">
                        <div className="flex justify-between">
                          <span className="flex items-center gap-1.5">
                            <Banknote className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                            현금
                          </span>
                          <span>{monthlySummary.cashAmount.toLocaleString()}원 · {monthlySummary.cashCount}건</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="flex items-center gap-1.5">
                            <CreditCard className="h-4 w-4 text-violet-600" aria-hidden="true" />
                            카드
                          </span>
                          <span>{monthlySummary.cardAmount.toLocaleString()}원 · {monthlySummary.cardCount}건</span>
                        </div>
                        <div className="flex justify-between text-slate-500">
                          <span>미기재</span>
                          <span>{monthlySummary.unspecifiedAmount.toLocaleString()}원 · {monthlySummary.unspecifiedCount}건</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        배송비 제외 · 세금 신고 전 확인용
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  {isLoading ? "월별 요약 정보를 불러오는 중..." : "이번 달 확정된 매출이 없습니다."}
                </div>
              )}
            </BlurredSectionWrapper>
          </div>
          
          {/* Daily Sales Calendar Section */}
          <div className="p-6">
            <h3 className="flex items-center text-lg font-semibold text-slate-700 mb-4">
              <CalendarDays className="h-5 w-5 mr-2 text-blue-600" />
              일별 매출 달력
            </h3>
            <BlurredSectionWrapper
              isAllowed={features.canViewDailySales}
              message="일별 매출 정보는 현재 플랜에서 제공되지 않습니다."
            >
              <div className="grid grid-cols-7 border-t border-l border-slate-200 rounded-lg overflow-hidden shadow-inner bg-slate-50">
                {daysOfWeek.map(dayName => (
                  <div key={dayName} className="py-2.5 text-center text-xs font-semibold text-slate-600 bg-slate-100 border-r border-b border-slate-200">
                    {dayName}
                  </div>
                ))}
                {renderCalendarDays()}
              </div>
            </BlurredSectionWrapper>
          </div>
        </div>

      {/* Premium Features Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200">
          <Card className="bg-white h-full">
            <CardHeader className="pb-4 pt-5">
              <CardTitle className="flex items-center text-lg font-semibold text-slate-700">
                <Users className="h-5 w-5 mr-2 text-purple-600" />
                단골 고객
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                정산서 데이터를 기준으로 가장 많이 주문한 고객 닉네임 순으로 상위 5명을 보여줍니다.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <BlurredSectionWrapper
                isAllowed={features.canViewLoyalCustomers}
                message="단골 고객 정보는 프리미엄 플랜부터 제공됩니다."
              >
                {/* Content for BlurredSectionWrapper */}
                {features.canViewLoyalCustomers ? (
                  // Feature is allowed: Show actual data or loading/no data message
                  actualLoyalCustomers.length > 0 ? (
                    <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 overflow-hidden max-h-[440px] overflow-y-auto">
                      {actualLoyalCustomers.map((customer, index) => (
                        <div key={index} className={`p-3 flex items-center space-x-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-grow flex justify-between items-center min-w-0">
                            <span className="text-sm font-medium text-slate-800 truncate" title={customer.customer_nickname}>
                              {customer.customer_nickname}
                            </span>
                            <span className="text-xs text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {customer.order_count}회
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-slate-500">
                      {isAnalyticsLoading ? '단골 고객 정보를 불러오는 중...' : '아직 단골 고객 데이터가 없습니다.'}
                    </div>
                  )
                ) : (
                  // Feature is NOT allowed: Show mock data (which will be blurred by the wrapper)
                  <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 overflow-hidden">
                    {mockLoyalCustomers.map((customer, index) => (
                      <div key={customer.rank} className={`p-3 flex items-center space-x-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold">
                          {customer.rank}
                        </div>
                        <div className="flex-grow flex justify-between items-center min-w-0">
                          <span className="text-sm font-medium text-slate-800 truncate" title={customer.name}>
                            {customer.name}
                          </span>
                          <span className="text-xs text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {customer.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </BlurredSectionWrapper>
            </CardContent>
          </Card>
        </div>
    
        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200">
          <Card className="bg-white h-full">
            <CardHeader className="pb-4 pt-5">
              <CardTitle className="flex items-center text-lg font-semibold text-slate-700">
                <ShoppingBag className="h-5 w-5 mr-2 text-green-600" />
                많이 주문된 상품
                <span className="ml-2 text-sm font-medium text-slate-500">
                  {format(currentDate, 'yyyy년 M월', { locale: ko })}
                </span>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                선택한 달의 정산서 데이터를 기준으로 가장 많이 주문된 상품 수량 순으로 상위 10개를 보여줍니다.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <BlurredSectionWrapper
                isAllowed={features.canViewTopItems}
                message="많이 주문된 상품 정보는 프리미엄 플랜부터 제공됩니다."
              >
                {/* Content for BlurredSectionWrapper */}
                {features.canViewTopItems ? (
                  // Feature is allowed: Show actual data or loading/no data message
                  topItemsError ? (
                    <div className="text-center py-10 text-slate-500">
                      많이 주문된 상품 정보를 불러오지 못했습니다.
                    </div>
                  ) : actualTopItems.length > 0 ? (
                    <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 overflow-hidden max-h-[440px] overflow-y-auto">
                      {actualTopItems.map((item, index) => (
                        <div key={index} className={`p-3 flex items-center space-x-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-grow flex justify-between items-center min-w-0">
                            <span className="text-sm font-medium text-slate-800 truncate" title={item.product_item_name}>
                              {item.product_item_name}
                            </span>
                            <span className="text-xs text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {item.total_quantity_sold}개
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-slate-500">
                      {isTopItemsLoading ? '많이 주문된 상품 정보를 불러오는 중...' : '이 달에 주문된 상품 데이터가 없습니다.'}
                    </div>
                  )
                ) : (
                  // Feature is NOT allowed: Show mock data (which will be blurred by the wrapper)
                  <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 overflow-hidden">
                    {mockTopItems.map((item, index) => (
                      <div key={item.rank} className={`p-3 flex items-center space-x-3 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold">
                          {item.rank}
                        </div>
                        <div className="flex-grow flex justify-between items-center min-w-0">
                          <span className="text-sm font-medium text-slate-800 truncate" title={item.name}>
                            {item.name}
                          </span>
                          <span className="text-xs text-slate-600 bg-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {item.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </BlurredSectionWrapper>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </div>
  );
}

// Helper function to get start of week based on locale (ko starts on Sunday)
// date-fns startOfWeek by default uses Monday if locale doesn't specify.
// For 'ko', it correctly uses Sunday.
const startOfWeek = (date: Date): Date => {
  const originalDate = new Date(date); // Create a copy to avoid mutating the original date
  const day = getDay(originalDate); // 0 for Sunday, 1 for Monday, ...
  const diff = day; // Difference to Sunday
  return new Date(originalDate.setDate(originalDate.getDate() - diff));
};

const endOfWeek = (date: Date): Date => {
  const originalDate = new Date(date); // Create a copy
  const day = getDay(originalDate);
  const diff = 6 - day; // Difference to Saturday
  return new Date(originalDate.setDate(originalDate.getDate() + diff));
};